import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const SB_URL  = import.meta.env.VITE_SUPABASE_URL  as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

type ReqStatus = "pending" | "approved" | "added";

interface CatalogueRequest {
  id: string;
  retailer_id: string;
  manufacturer_id: string | null;
  custom_manufacturer_name: string | null;
  category: string | null;
  status: ReqStatus;
  created_at: string;
  /* denormalised after fetch */
  store_name: string;
  retailer_email: string | null;
  owner_user_id: string | null;
  owner_first_name: string | null;
  owner_last_name: string | null;
  manufacturer_name: string | null;
}

/* Raw shape from Supabase — no embedded joins */
interface RawRequest {
  id: string;
  retailer_id: string;
  manufacturer_id: string | null;
  custom_manufacturer_name: string | null;
  category: string | null;
  status: ReqStatus;
  created_at: string;
}

interface RawProfile {
  id: string;
  first_name: string | null;
  last_name:  string | null;
}

type StatusFilter = "all" | ReqStatus;

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const CATEGORY_LABELS: Record<string, string> = {
  gas_fireplace: "Gas Fireplace", wood_fireplace: "Wood Fireplace",
  pellet_stove: "Pellet Stove", electric_fireplace: "Electric Fireplace",
  fireplace_insert: "Fireplace Insert", gas_logs: "Gas Logs",
  chimney_venting: "Chimney & Venting", fireplace_accessory: "Hearth Accessory",
  patio_furniture: "Patio Furniture", outdoor_kitchen: "Outdoor Kitchen",
  pergola_gazebo: "Pergola / Gazebo", fire_pit: "Fire Pit",
  patio_heater: "Patio Heater", outdoor_accessory: "Outdoor Accessory",
  gas_grill: "Gas Grill", charcoal_grill: "Charcoal Grill",
  pellet_grill: "Pellet Grill", kamado_grill: "Kamado Grill",
  built_in_grill: "Built-in Grill", bbq_accessory: "BBQ Accessory",
};

const STATUS_LABELS: Record<ReqStatus, string> = {
  pending:  "Pending",
  approved: "Approved",
  added:    "Added",
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

const inputCls =
  "w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 outline-none focus:border-orange-500 transition";

function StatusPill({ status }: { status: ReqStatus }) {
  const cls: Record<ReqStatus, string> = {
    pending:  "border-orange-700 bg-orange-900/30 text-orange-400",
    approved: "border-blue-800  bg-blue-900/30  text-blue-400",
    added:    "border-green-800 bg-green-900/30 text-green-400",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Inline status selector                                              */
/* ------------------------------------------------------------------ */

function StatusSelect({
  value,
  saving,
  onChange,
}: {
  value: ReqStatus;
  saving: boolean;
  onChange: (s: ReqStatus) => void;
}) {
  const borderCls: Record<ReqStatus, string> = {
    pending:  "border-orange-700 text-orange-400",
    approved: "border-blue-700  text-blue-400",
    added:    "border-green-700 text-green-400",
  };
  return (
    <div className="relative">
      <select
        disabled={saving}
        value={value}
        onChange={(e) => onChange(e.target.value as ReqStatus)}
        className={`rounded-lg border bg-gray-900 pl-2.5 pr-7 py-1 text-xs font-medium outline-none focus:ring-1 focus:ring-orange-500 transition disabled:opacity-50 cursor-pointer ${borderCls[value]}`}
      >
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="added">Added</option>
      </select>
      {saving && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full border-2 border-orange-400 border-t-transparent animate-spin" />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminCatalogueRequests() {
  const { session } = useAuth();
  const { toast }   = useToast();
  const jwt = session?.access_token ?? "";

  const [requests, setRequests]     = useState<CatalogueRequest[]>([]);
  const [loading,  setLoading]      = useState(true);
  const [error,    setError]        = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search,       setSearch]       = useState("");

  /* Per-row saving state */
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  /* Bulk select */
  const [selectedIds,  setSelectedIds]  = useState<Set<string>>(new Set());
  const [bulkStatus,   setBulkStatus]   = useState<ReqStatus>("approved");
  const [bulkSaving,   setBulkSaving]   = useState(false);

  /* ── Fetch ─────────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    setError(null);
    try {
      /* Step 1 — base requests, no embedded joins */
      const rRes = await fetch(
        `${SB_URL}/rest/v1/retailer_manufacturer_requests` +
          `?select=id,retailer_id,manufacturer_id,custom_manufacturer_name,category,status,created_at` +
          `&order=created_at.desc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
      );
      if (!rRes.ok) {
        const body = await rRes.text();
        console.error("[catalogue] requests fetch failed", rRes.status, body);
        throw new Error(`Requests fetch failed: ${rRes.status} — ${body}`);
      }
      const raw: RawRequest[] = await rRes.json();

      /* Step 2 — retailers by id */
      const retailerIds = [...new Set(raw.map((r) => r.retailer_id).filter(Boolean))];
      type RetailerRow = { id: string; store_name: string; email: string | null; owner_user_id: string | null };
      let retailerMap = new Map<string, RetailerRow>();
      if (retailerIds.length > 0) {
        const retRes = await fetch(
          `${SB_URL}/rest/v1/retailers` +
            `?select=id,store_name,email,owner_user_id` +
            `&id=in.(${retailerIds.join(",")})`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
        );
        if (retRes.ok) {
          const rows: RetailerRow[] = await retRes.json();
          retailerMap = new Map(rows.map((r) => [r.id, r]));
        } else {
          console.error("[catalogue] retailers fetch failed", retRes.status, await retRes.text());
        }
      }

      /* Step 3 — manufacturers by id */
      const mfrIds = [...new Set(raw.map((r) => r.manufacturer_id).filter((id): id is string => Boolean(id)))];
      type MfrRow = { id: string; name: string };
      let mfrMap = new Map<string, MfrRow>();
      if (mfrIds.length > 0) {
        const mfrRes = await fetch(
          `${SB_URL}/rest/v1/manufacturers` +
            `?select=id,name` +
            `&id=in.(${mfrIds.join(",")})`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
        );
        if (mfrRes.ok) {
          const rows: MfrRow[] = await mfrRes.json();
          mfrMap = new Map(rows.map((m) => [m.id, m]));
        } else {
          console.error("[catalogue] manufacturers fetch failed", mfrRes.status, await mfrRes.text());
        }
      }

      /* Step 4 — profiles for owner_user_ids */
      const ownerIds = [
        ...new Set(
          [...retailerMap.values()]
            .map((r) => r.owner_user_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ];
      let profileMap = new Map<string, RawProfile>();
      if (ownerIds.length > 0) {
        const pRes = await fetch(
          `${SB_URL}/rest/v1/profiles` +
            `?select=id,first_name,last_name` +
            `&id=in.(${ownerIds.join(",")})`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
        );
        if (pRes.ok) {
          const profiles: RawProfile[] = await pRes.json();
          profileMap = new Map(profiles.map((p) => [p.id, p]));
        } else {
          console.error("[catalogue] profiles fetch failed", pRes.status, await pRes.text());
        }
      }

      /* Step 5 — client-side join */
      const joined: CatalogueRequest[] = raw.map((r) => {
        const retailer  = retailerMap.get(r.retailer_id) ?? null;
        const ownerId   = retailer?.owner_user_id ?? null;
        const profile   = ownerId ? profileMap.get(ownerId) : undefined;
        const mfr       = r.manufacturer_id ? mfrMap.get(r.manufacturer_id) : null;
        return {
          id:                       r.id,
          retailer_id:              r.retailer_id,
          manufacturer_id:          r.manufacturer_id,
          custom_manufacturer_name: r.custom_manufacturer_name,
          category:                 r.category,
          status:                   r.status,
          created_at:               r.created_at,
          store_name:               retailer?.store_name ?? "Unknown retailer",
          retailer_email:           retailer?.email ?? null,
          owner_user_id:            ownerId,
          owner_first_name:         profile?.first_name ?? null,
          owner_last_name:          profile?.last_name  ?? null,
          manufacturer_name:        mfr?.name ?? null,
        };
      });

      setRequests(joined);
    } catch (e) {
      console.error("[catalogue] load error", e);
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { load(); }, [load]);

  /* ── Notify retailer (fire-and-forget) ──────────────────────────── */
  const notifyAdded = useCallback(
    async (req: CatalogueRequest) => {
      if (!req.retailer_email) return;
      const mfrName = req.manufacturer_name ?? req.custom_manufacturer_name ?? "the manufacturer";
      try {
        await fetch(`${SB_URL}/functions/v1/notify-catalogue-added`, {
          method: "POST",
          headers: {
            Authorization:  `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            retailer_email:    req.retailer_email,
            store_name:        req.store_name,
            manufacturer_name: mfrName,
          }),
        });
      } catch (err) {
        console.warn("[notify-catalogue-added]", err);
        // Non-fatal — status was already saved
      }
    },
    [jwt],
  );

  /* ── Single row status change ───────────────────────────────────── */
  const handleStatusChange = useCallback(
    async (id: string, newStatus: ReqStatus) => {
      const req = requests.find((r) => r.id === id);
      if (!req || req.status === newStatus) return;

      setSavingIds((prev) => new Set(prev).add(id));
      try {
        const res = await fetch(
          `${SB_URL}/rest/v1/retailer_manufacturer_requests?id=eq.${id}`,
          {
            method:  "PATCH",
            headers: {
              apikey:           ANON_KEY,
              Authorization:    `Bearer ${jwt}`,
              "Content-Type":   "application/json",
              Prefer:           "return=minimal",
            },
            body: JSON.stringify({ status: newStatus }),
          },
        );
        if (!res.ok) throw new Error(`${res.status}`);

        /* Update local state immediately */
        const updated = { ...req, status: newStatus };
        setRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));

        if (newStatus === "added") void notifyAdded(updated);

        toast({ title: "Status updated" });
      } catch (e) {
        toast({
          title:       "Update failed",
          description: e instanceof Error ? e.message : "Unknown error",
          variant:     "destructive",
        });
      } finally {
        setSavingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      }
    },
    [jwt, requests, notifyAdded, toast],
  );

  /* ── Bulk status update ─────────────────────────────────────────── */
  const handleBulkApply = useCallback(async () => {
    const toUpdate = requests.filter(
      (r) => selectedIds.has(r.id) && r.status !== bulkStatus,
    );
    if (toUpdate.length === 0) {
      toast({ title: "Nothing to update", description: "All selected rows already have that status." });
      return;
    }
    setBulkSaving(true);
    try {
      const ids = toUpdate.map((r) => r.id).join(",");
      const res = await fetch(
        `${SB_URL}/rest/v1/retailer_manufacturer_requests?id=in.(${ids})`,
        {
          method:  "PATCH",
          headers: {
            apikey:           ANON_KEY,
            Authorization:    `Bearer ${jwt}`,
            "Content-Type":   "application/json",
            Prefer:           "return=minimal",
          },
          body: JSON.stringify({ status: bulkStatus }),
        },
      );
      if (!res.ok) throw new Error(`${res.status}`);

      const updatedSet = new Set(toUpdate.map((r) => r.id));
      const updatedReqs = toUpdate.map((r) => ({ ...r, status: bulkStatus }));

      setRequests((prev) =>
        prev.map((r) => (updatedSet.has(r.id) ? { ...r, status: bulkStatus } : r)),
      );

      if (bulkStatus === "added") {
        void Promise.allSettled(updatedReqs.map((r) => notifyAdded(r)));
      }

      toast({
        title: `${toUpdate.length} request${toUpdate.length !== 1 ? "s" : ""} updated`,
      });
      setSelectedIds(new Set());
    } catch (e) {
      toast({
        title:       "Bulk update failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant:     "destructive",
      });
    } finally {
      setBulkSaving(false);
    }
  }, [jwt, requests, selectedIds, bulkStatus, notifyAdded, toast]);

  /* ── Derived ─────────────────────────────────────────────────────── */
  const counts = useMemo(
    () => ({
      all:      requests.length,
      pending:  requests.filter((r) => r.status === "pending").length,
      approved: requests.filter((r) => r.status === "approved").length,
      added:    requests.filter((r) => r.status === "added").length,
    }),
    [requests],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      const mfrName = (r.manufacturer_name ?? r.custom_manufacturer_name ?? "").toLowerCase();
      return (
        r.store_name.toLowerCase().includes(q) ||
        mfrName.includes(q)
      );
    });
  }, [requests, statusFilter, search]);

  /* Select-all for current filtered view */
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((r) => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((r) => next.add(r.id));
        return next;
      });
    }
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  /* ── Tab bar data ─────────────────────────────────────────────────── */
  const STATUS_TABS: { key: StatusFilter; label: string }[] = [
    { key: "all",      label: `All (${counts.all})`           },
    { key: "pending",  label: `Pending (${counts.pending})`   },
    { key: "approved", label: `Approved (${counts.approved})` },
    { key: "added",    label: `Added (${counts.added})`       },
  ];

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Catalogue Requests</h1>
        <button
          onClick={load}
          className="rounded-lg border border-gray-700 px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-800 transition"
        >
          Refresh
        </button>
      </div>

      {/* Stats row */}
      {!loading && (
        <div className="flex flex-wrap gap-3">
          {[
            { label: "Total",    value: counts.all,      color: "text-gray-100" },
            { label: "Pending",  value: counts.pending,  color: "text-orange-400" },
            { label: "Approved", value: counts.approved, color: "text-blue-400" },
            { label: "Added",    value: counts.added,    color: "text-green-400" },
          ].map(({ label, value, color }) => (
            <div
              key={label}
              className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3 min-w-[90px]"
            >
              <p className="text-xs text-gray-500 mb-0.5">{label}</p>
              <p className={`text-xl font-semibold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Status tab filter */}
      <div className="flex gap-0 border-b border-gray-800">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setStatusFilter(key); setSelectedIds(new Set()); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              statusFilter === key
                ? "border-orange-500 text-orange-400"
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        className="w-full max-w-xs rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 outline-none focus:border-orange-500 transition"
        placeholder="Search retailer or manufacturer…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-orange-800/50 bg-orange-900/10 px-4 py-3">
          <span className="text-sm font-medium text-orange-300">
            {selectedIds.size} selected
          </span>
          <span className="text-gray-600">·</span>
          <label className="text-xs text-gray-400">Set status to</label>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value as ReqStatus)}
            className="rounded-lg border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm text-gray-100 outline-none focus:border-orange-500 transition"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="added">Added</option>
          </select>
          <button
            onClick={handleBulkApply}
            disabled={bulkSaving}
            className="rounded-lg bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50 transition"
          >
            {bulkSaving ? "Saving…" : "Apply"}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-gray-500 hover:text-gray-300 transition"
          >
            Clear
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-900/40 bg-red-900/20 px-4 py-3 text-sm text-red-400">
          {error}
          <button onClick={load} className="ml-3 underline hover:text-red-300">
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-12 rounded-xl border border-gray-800 bg-gray-900 animate-pulse"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center space-y-1">
          <p className="text-sm text-gray-500">
            {requests.length === 0
              ? "No catalogue requests yet."
              : "No requests match your filters."}
          </p>
          {(search || statusFilter !== "all") && (
            <button
              onClick={() => { setSearch(""); setStatusFilter("all"); }}
              className="text-xs text-orange-400 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60">
              <tr className="border-b border-gray-800">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-600 bg-gray-800 text-orange-500 focus:ring-orange-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Retailer</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 hidden md:table-cell">
                  Owner
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Manufacturer</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 hidden lg:table-cell">
                  Category
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 hidden md:table-cell">
                  Submitted
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const ownerName =
                  [r.owner_first_name, r.owner_last_name].filter(Boolean).join(" ") || "—";
                const mfrDisplay =
                  r.manufacturer_name ?? r.custom_manufacturer_name ?? (
                    <span className="text-gray-600 italic">Unknown</span>
                  );
                const catLabel =
                  CATEGORY_LABELS[r.category ?? ""] ?? r.category ?? "—";

                return (
                  <tr
                    key={r.id}
                    className={`border-t border-gray-800 transition ${
                      selectedIds.has(r.id)
                        ? "bg-orange-950/20"
                        : "hover:bg-gray-900/40"
                    }`}
                  >
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleRow(r.id)}
                        className="rounded border-gray-600 bg-gray-800 text-orange-500 focus:ring-orange-500 cursor-pointer"
                      />
                    </td>

                    {/* Retailer */}
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-100">{r.store_name}</p>
                      {r.retailer_email && (
                        <p className="text-xs text-gray-500">{r.retailer_email}</p>
                      )}
                    </td>

                    {/* Owner */}
                    <td className="px-4 py-3 text-gray-400 hidden md:table-cell">
                      {ownerName}
                    </td>

                    {/* Manufacturer */}
                    <td className="px-4 py-3">
                      <p className="text-gray-200">{mfrDisplay}</p>
                      {!r.manufacturer_id && r.custom_manufacturer_name && (
                        <p className="text-xs text-gray-600">Custom request</p>
                      )}
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3 text-gray-400 text-xs hidden lg:table-cell">
                      {catLabel}
                    </td>

                    {/* Status inline dropdown */}
                    <td className="px-4 py-3">
                      <StatusSelect
                        value={r.status}
                        saving={savingIds.has(r.id)}
                        onChange={(s) => handleStatusChange(r.id, s)}
                      />
                    </td>

                    {/* Date submitted */}
                    <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day:   "numeric",
                        year:  "numeric",
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
